import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { AlertCircle, ExternalLink, Search, Wheat, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  countryDashboard,
  listAdministrativeAreas,
  listAggregators,
  listAllCommodities,
  listCommodities,
  listCommoditiesReset,
  listUnitsOfMeasure,
  updateCommodity,
} from "../../actions";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { WFPButton } from "../../components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../components/ui/input-group";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import TimeRangeTabs from "../../components/TimeRangeTabs";
import { DEFAULT_TIME_RANGE_PERIOD, type TimeRangePeriod } from "../../constants/timeRange";
import { getCountryObjectFromCache } from "../../utils";
import { isExternalUser, type UserData } from "../../utils/roles";
import { adaptCommoditiesList } from "../../utils/adapters";
import { orderByLocalName } from "../../utils/i18n";
import { getTransifexLink } from "../../utils/transifex";
import { buildAdminAreaTreeOptions } from "../../utils/adminArea";
import { CommoditiesFilters } from "./components/CommoditiesFilters";
import { CommoditiesSortMenu } from "./components/CommoditiesSortMenu";
import { RemoveCommodityDialog } from "./components/RemoveCommodityDialog";
import { List, type Commodity, type CommodityListItem } from "./components/List";
import { type ComboboxOption } from "../InfoHub/Components/Form/Components/MultiSelectCombobox";
import {
  type AdministrativeArea,
  type AggregatorOption,
  type CommoditiesDashboardParams,
  type CommoditiesFilterState,
  type CountryDashboardData,
  type EntityId,
  type UnitOfMeasure,
} from "./components/List/components/types";
import {
  buildCommodityStatsMap,
  getDepositsCount,
  getFarmersWithCommodity,
  getQuantitySoldTotal,
} from "./components/List/components/utils";

interface ListCommoditiesState {
  isFetching: boolean;
  errorMessage: string;
  data: { results: CommodityListItem[] };
}

interface ListUnitsOfMeasureState {
  isFetching: boolean;
  errorMessage: string;
  data: { results: UnitOfMeasure[] };
}

interface CountryDashboardState {
  isFetching: boolean;
  errorMessage: string;
  data: CountryDashboardData;
}

interface ResourceListState<TResult> {
  isFetching: boolean;
  errorMessage: string;
  data: { results: TResult[] };
}

interface RootState {
  countryDashboard: CountryDashboardState;
  listAdministrativeAreas: ResourceListState<AdministrativeArea>;
  listAggregators: ResourceListState<AggregatorOption>;
  listCommodities: ListCommoditiesState;
  listUnitsOfMeasure: ListUnitsOfMeasureState;
  userDetail: { data: UserData };
}

interface StateProps {
  administrativeAreas: AdministrativeArea[];
  aggregators: AggregatorOption[];
  areAggregatorsLoading: boolean;
  areLocationsLoading: boolean;
  commodities: CommodityListItem[];
  countryDashboardData: CountryDashboardData;
  currentUser: UserData;
  errorMessage: string;
  isFetching: boolean;
  unitsOfMeasure: UnitOfMeasure[];
}

interface ApiResponse<TData extends Record<string, unknown> = Record<string, unknown>> {
  data: TData;
}

type ApiResult = ApiResponse | null | undefined;
type ApiPromise = Promise<ApiResult>;

interface DispatchProps {
  getCountryDashboardData: (params?: CommoditiesDashboardParams) => void;
  listAdministrativeAreas: () => void;
  listAggregators: () => void;
  listAllCommodities: () => void;
  listCommodities: () => void;
  listCommoditiesReset: () => void;
  listUnitsOfMeasure: () => void;
  updateCommodity: (commodity: Commodity) => ApiPromise;
}

type CommoditiesProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

const INITIAL_FILTERS: CommoditiesFilterState = {
  period: DEFAULT_TIME_RANGE_PERIOD,
  aggregatorId: null,
  administrativeAreaId: null,
};

// The shared time-range control speaks day/week/month/all_time; the country_dashboard
// endpoint expects a `days` window (all_time = no window).
const PERIOD_TO_DAYS: Record<TimeRangePeriod, number | null> = {
  day: 1,
  week: 7,
  month: 30,
  all_time: null,
};

// The commodity + which action failed, so the alert can name it (e.g. "Unable to
// deactivate {commodity} for {country}"). The update thunk resolves to `undefined` on
// failure, so we detect it from the resolved value rather than the redux error slice.
type CommodityActionType = "activate" | "deactivate";

interface FailedCommodityAction {
  commodityName: string;
  type: CommodityActionType;
}

// Drops unset filters so the country_dashboard request only carries active ones.
const buildDashboardParams = (filters: CommoditiesFilterState): CommoditiesDashboardParams => {
  const params: CommoditiesDashboardParams = {};
  const days = PERIOD_TO_DAYS[filters.period];

  if (days !== null) {
    params.days = days;
  }
  if (filters.aggregatorId !== null) {
    params.aggregator = filters.aggregatorId;
  }
  if (filters.administrativeAreaId !== null) {
    params.administrative_area = filters.administrativeAreaId;
  }

  return params;
};

// This component is used to display the list of commodities for the WFP Manager
export const Commodities = ({
  administrativeAreas,
  aggregators,
  areAggregatorsLoading,
  areLocationsLoading,
  commodities,
  countryDashboardData,
  currentUser,
  errorMessage,
  getCountryDashboardData,
  isFetching,
  listAdministrativeAreas,
  listAggregators,
  listAllCommodities,
  listCommodities,
  listCommoditiesReset,
  listUnitsOfMeasure,
  unitsOfMeasure,
  updateCommodity,
}: CommoditiesProps) => {
  const { t } = useTranslation();
  // External users get a read-only, country-scoped view: no "Manage all" tab (the only
  // place activate/deactivate controls live) and no commodity translations shortcut.
  const isReadOnly = isExternalUser(currentUser);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<CommoditiesFilterState>(INITIAL_FILTERS);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [commodityPendingRemoval, setCommodityPendingRemoval] = useState<CommodityListItem | null>(
    null
  );
  const [failedAction, setFailedAction] = useState<FailedCommodityAction | null>(null);

  const dashboardParams = useMemo(() => buildDashboardParams(filters), [filters]);

  const onSortingChange = (updater: Updater<SortingState>) =>
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );

  // "Show all" swaps the country-scoped list for every commodity registered in Farm2Go
  // (all countries) via the /everything/ endpoint. Both write the same COMMODITIES slice.
  const refetchCommodities = useCallback(() => {
    if (showAll) {
      listAllCommodities();
    } else {
      listCommodities();
    }
  }, [showAll, listAllCommodities, listCommodities]);

  useEffect(() => {
    listUnitsOfMeasure();
    listAggregators();
    listAdministrativeAreas();
  }, [listUnitsOfMeasure, listAggregators, listAdministrativeAreas]);

  // Re-fetch the dashboard whenever the overview filters change (the metric columns are
  // server-aggregated, so filtering happens on the backend).
  useEffect(() => {
    getCountryDashboardData(dashboardParams);
  }, [getCountryDashboardData, dashboardParams]);

  useEffect(() => {
    refetchCommodities();

    return () => {
      listCommoditiesReset();
    };
  }, [refetchCommodities, listCommoditiesReset]);

  const updateCommodityCountries = (
    commodity: CommodityListItem,
    countries: EntityId[],
    action: CommodityActionType
  ) => {
    const commodityToPut: Commodity = {
      ...commodity.dto,
      countries,
    };

    // The update thunk resolves to the response on success and to `undefined` on failure
    // (it swallows the rejection). So a successful PUT refetches the list and clears any
    // prior error; a failed one leaves the list untouched and shows the alert below,
    // naming the commodity and action.
    updateCommodity(commodityToPut).then(resp => {
      if (resp?.data) {
        setFailedAction(null);
        refetchCommodities();
      } else {
        setFailedAction({ commodityName: commodity.name, type: action });
      }
    });

    // Intentionally not chained onto the promise above: the dashboard is refetched
    // immediately regardless of whether the update above succeeds, fails, or is still
    // pending. Pre-existing behavior, preserved as-is.
    getCountryDashboardData(dashboardParams);
  };

  const onClickAddCommodity = (commodity: CommodityListItem) => {
    const countryToAdd = getCountryObjectFromCache();
    const updatedCountryList = [...new Set(commodity.countries).add(countryToAdd.id).values()];

    updateCommodityCountries(commodity, updatedCountryList, "activate");
  };

  const confirmRemoveCommodity = () => {
    if (!commodityPendingRemoval) {
      return;
    }

    const countryToRemove = getCountryObjectFromCache();
    const updatedCountryList = commodityPendingRemoval.countries.filter(
      countryId => countryId !== countryToRemove.id
    );

    updateCommodityCountries(commodityPendingRemoval, updatedCountryList, "deactivate");
    setCommodityPendingRemoval(null);
  };

  const clearFilter = () => {
    setSearchTerm("");
  };

  const onFiltersChange = (patch: Partial<CommoditiesFilterState>) =>
    setFilters(current => ({ ...current, ...patch }));

  // Switching tabs resets the sort so the manage view always re-applies its
  // available-in-country-first default, and clears any stale update error.
  const onTabChange = (value: string) => {
    setShowAll(value === "all");
    setSorting([]);
    setFailedAction(null);
  };

  const currentCountry = getCountryObjectFromCache();
  const countryName = currentCountry.name;
  const transifexLink = getTransifexLink("backend");

  const aggregatorOptions = useMemo<ComboboxOption[]>(
    () =>
      aggregators
        .map(aggregator => ({ value: aggregator.id, label: aggregator.name }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [aggregators]
  );

  const locationOptions = useMemo<ComboboxOption[]>(
    () => buildAdminAreaTreeOptions(administrativeAreas),
    [administrativeAreas]
  );

  const statsMap = useMemo(
    () => buildCommodityStatsMap(countryDashboardData),
    [countryDashboardData]
  );
  const pendingRemovalStat = commodityPendingRemoval
    ? statsMap.get(commodityPendingRemoval.id)
    : undefined;
  const pendingRemovalFarmers = getFarmersWithCommodity(pendingRemovalStat);
  // Heuristic mirror of the backend "in use" rule (has deposits or offers): a warning
  // that removal will be rejected while the commodity is in use. The backend stays the
  // source of truth — it also sees non-exchange offers the dashboard does not expose.
  const pendingRemovalInUse =
    getDepositsCount(pendingRemovalStat) > 0 || getQuantitySoldTotal(pendingRemovalStat) > 0;

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
            <Wheat />
          </span>
          <h1 className="font-semibold text-2xl">{t("commodities.overviewTitle")}</h1>
        </div>
        {showAll && transifexLink && (
          <WFPButton asChild>
            <a href={transifexLink} target="_blank" rel="noopener noreferrer">
              <span>{t("commodities.commodityTranslations")}</span>
              <ExternalLink aria-hidden="true" className="size-4" />
            </a>
          </WFPButton>
        )}
      </div>

      {!isReadOnly && (
        <Tabs value={showAll ? "all" : "country"} onValueChange={onTabChange}>
          <TabsList>
            <TabsTrigger value="country">
              {t("commodities.tabs.country", { country: countryName })}
            </TabsTrigger>
            <TabsTrigger value="all">{t("commodities.tabs.manageAll")}</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="flex flex-col flex-wrap items-stretch gap-4 sm:flex-row sm:items-center">
        <div className="w-full sm:w-80">
          <InputGroup>
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="commodity-name-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("commodities.searchPlaceholder")}
              aria-label={t("commodities.searchPlaceholder")}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("commodities.clearFilter")}
                  onClick={clearFilter}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
        </div>
        {!showAll && (
          <>
            <CommoditiesSortMenu sorting={sorting} onSortingChange={onSortingChange} t={t} />
            <CommoditiesFilters
              aggregatorOptions={aggregatorOptions}
              areAggregatorsLoading={areAggregatorsLoading}
              areLocationsLoading={areLocationsLoading}
              filters={filters}
              locationOptions={locationOptions}
              onChange={onFiltersChange}
              t={t}
            />
            <TimeRangeTabs
              className="shrink-0 sm:ml-auto"
              value={filters.period}
              onValueChange={period => onFiltersChange({ period })}
              ariaLabel={t("commodities.timeRange.ariaLabel")}
            />
          </>
        )}
      </div>

      {showAll && (
        <p className="text-sm text-muted-foreground">{t("commodities.manageDescription")}</p>
      )}

      {failedAction && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>
            {t(`commodities.${failedAction.type}Error.title`, {
              commodity: failedAction.commodityName,
              country: countryName,
            })}
          </AlertTitle>
          <AlertDescription>{t(`commodities.${failedAction.type}Error.body`)}</AlertDescription>
          <WFPButton
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t("commodities.dismissError")}
            className="absolute end-3 top-3"
            onClick={() => setFailedAction(null)}
          >
            <X className="size-3.5" aria-hidden="true" />
          </WFPButton>
        </Alert>
      )}

      <List
        commodities={commodities}
        countryDashboardData={countryDashboardData}
        errorMessage={errorMessage}
        isFetching={isFetching}
        onClickAddCommodity={onClickAddCommodity}
        onSortingChange={onSortingChange}
        searchTerm={searchTerm}
        onClickRemoveCommodity={setCommodityPendingRemoval}
        showAll={showAll}
        sorting={sorting}
        unitsOfMeasure={unitsOfMeasure}
      />

      <RemoveCommodityDialog
        commodityName={commodityPendingRemoval?.name}
        countryName={countryName}
        farmersCount={pendingRemovalFarmers}
        inUse={pendingRemovalInUse}
        open={commodityPendingRemoval !== null}
        onCancel={() => setCommodityPendingRemoval(null)}
        onConfirm={confirmRemoveCommodity}
        t={t}
      />
    </main>
  );
};

// Starting from the redux state it gets data related to logged in user
export const mapStateToProps = (state: RootState): StateProps => {
  return {
    // Commodities management
    isFetching: state.listCommodities.isFetching || state.countryDashboard.isFetching,
    errorMessage: state.listCommodities.errorMessage,
    commodities: adaptCommoditiesList(
      orderByLocalName(state.listCommodities.data.results),
      state.listUnitsOfMeasure.data.results
    ) as CommodityListItem[],
    countryDashboardData: state.countryDashboard.data,
    currentUser: state.userDetail?.data ?? { user_groups: [] },
    unitsOfMeasure: state.listUnitsOfMeasure.data.results,
    aggregators: state.listAggregators.data.results,
    administrativeAreas: state.listAdministrativeAreas.data.results,
    areAggregatorsLoading: state.listAggregators.isFetching,
    areLocationsLoading: state.listAdministrativeAreas.isFetching,
  };
};

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listCommodities: () => dispatchAction(dispatch, listCommodities()),
    listAllCommodities: () => dispatchAction(dispatch, listAllCommodities()),
    updateCommodity: commodity =>
      dispatchAction<ApiPromise>(dispatch, updateCommodity(commodity.id, commodity)),
    listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
    listCommoditiesReset: () => dispatchAction(dispatch, listCommoditiesReset()),
    getCountryDashboardData: params =>
      dispatchAction(dispatch, countryDashboard("", "", false, params ?? {})),
    listAggregators: () => dispatchAction(dispatch, listAggregators({ page_size: 9999 })),
    listAdministrativeAreas: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreas({
          page_size: 9999,
          page: 1,
          country: getCountryObjectFromCache().id,
        })
      ),
  };
};

// The component uses the redux store
export const ConnectedCommodities = connect(mapStateToProps, mapDispatchToProps)(Commodities);
